import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { productAPI } from '../services/api';

const ProductsContext = createContext({});

export const useProducts = () => useContext(ProductsContext);

export const ProductsProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProducts = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await productAPI.getProducts(params);
      const productsData = result.success ? result.data?.products || result.data : 
                          result.data ? result.data.products || result.data : 
                          Array.isArray(result) ? result : [];
      setProducts(productsData || []);
      return { success: true, data: productsData };
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setError(error.message || 'Failed to fetch products');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFeaturedProducts = useCallback(async (limit = 8) => {
    try {
      const result = await productAPI.getFeaturedProducts(limit);
      const featuredData = result.success ? result.data : 
                          result.data ? result.data : 
                          Array.isArray(result) ? result : [];
      setFeaturedProducts(featuredData || []);
      return { success: true, data: featuredData };
    } catch (error) {
      console.error('Failed to fetch featured products:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const fetchBestSellers = useCallback(async (limit = 8) => {
    try {
      const result = await productAPI.getBestSellers(limit);
      const bestSellersData = result.success ? result.data : 
                             result.data ? result.data : 
                             Array.isArray(result) ? result : [];
      setBestSellers(bestSellersData || []);
      return { success: true, data: bestSellersData };
    } catch (error) {
      console.error('Failed to fetch best sellers:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const fetchNewProducts = useCallback(async (limit = 8) => {
    try {
      const result = await productAPI.getNewProducts(limit);
      const newProductsData = result.success ? result.data : 
                             result.data ? result.data : 
                             Array.isArray(result) ? result : [];
      setNewProducts(newProductsData || []);
      return { success: true, data: newProductsData };
    } catch (error) {
      console.error('Failed to fetch new products:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const result = await productAPI.getCategories();
      const categoriesData = result.success ? result.data : 
                            result.data ? result.data : 
                            Array.isArray(result) ? result : [];
      setCategories(categoriesData || []);
      return { success: true, data: categoriesData };
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const getProduct = async (id) => {
    try {
      const result = await productAPI.getProduct(id);
      return result;
    } catch (error) {
      console.error('Failed to get product:', error);
      return { success: false, error: error.message };
    }
  };

  const addProduct = async (productData) => {
    try {
      const result = await productAPI.createProduct(productData);
      if (result.success) {
        await fetchProducts(); // Refresh the list
      }
      return result;
    } catch (error) {
      console.error('Failed to create product:', error);
      return { success: false, error: error.message };
    }
  };

  const updateProduct = async (id, productData) => {
    try {
      const result = await productAPI.updateProduct(id, productData);
      if (result.success) {
        await fetchProducts();
      }
      return result;
    } catch (error) {
      console.error('Failed to update product:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteProduct = async (id) => {
    try {
      const result = await productAPI.deleteProduct(id);
      if (result.success) {
        setProducts(prev => prev.filter(product => product.id !== id));
      }
      return result;
    } catch (error) {
      console.error('Failed to delete product:', error);
      return { success: false, error: error.message };
    }
  };

  // Initialize on mount
  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchFeaturedProducts();
    fetchBestSellers();
    fetchNewProducts();
  }, [fetchProducts, fetchCategories, fetchFeaturedProducts, fetchBestSellers, fetchNewProducts]);

  const value = {
    products,
    featuredProducts,
    bestSellers,
    newProducts,
    categories,
    loading,
    error,
    fetchProducts,
    fetchFeaturedProducts,
    fetchBestSellers,
    fetchNewProducts,
    fetchCategories,
    getProduct,
    addProduct,
    updateProduct,
    deleteProduct,
  };

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
};